// Table — présentation tabulaire HTML PURE, composable en JSX. Aucune directive client, aucun hook, aucun contexte, aucun
// mouvement : se rend sur le serveur (RSC) comme dans un composant client. Peinture = celle de la DataTable
// (`table.module.css`, une seule définition).
//
// Rôles distincts, à ne pas confondre :
//  · `Table` (ici) — présenter des lignes DÉJÀ calculées : le consommateur fait sa `map`, ses liens (`<a href>` résolu,
//    `LinkLike`), ses dates formatées, ses totaux et son tri AVANT ou PENDANT son rendu serveur. Rien n'est interprété.
//  · `DataTable` / `SortableDataTable` (`@ubm/product-ui`) — moteur client : tri interactif, sélection, largeurs,
//    gel, édition, clavier. Lui passer des fonctions depuis le serveur crée une frontière client : pas pour ces cas.
import type { ComponentProps, CSSProperties, ReactNode } from "react";
import { joinClasses, tablePresentation as paint, tableStructure as structure } from "./table-presentation";

export type TableDensity = "default" | "compact";

export interface TableProps extends ComponentProps<"table"> {
  /** `compact` : rangées de 32, cellules de 8, filets verticaux (densité utilitaire de la DataTable). Défaut `default`. */
  density?: TableDensity;
  /**
   * Largeur sous laquelle la table DÉFILE (dans `<TableContainer>`) au lieu d'écraser ses colonnes à leur largeur minimale
   * (identifiants coupés, descriptions d'un mot par ligne). Choisie par le consommateur ; rien n'est mesuré. Absente : 100 %.
   */
  minWidth?: CSSProperties["minWidth"];
}

/** `<table>` peint. Nom accessible : `<TableCaption>` ou `aria-label` / `aria-labelledby` du consommateur. */
export function Table({ density = "default", minWidth, className, style, ...props }: TableProps) {
  return (
    <table
      {...props}
      style={minWidth === undefined ? style : { ...style, minWidth }}
      data-density={density === "compact" ? "compact" : undefined}
      className={joinClasses(paint.root, density === "compact" && paint.compact, className)}
    />
  );
}

/** `<thead>` : ses `<tr>`/`<TableHead>` prennent la peinture d'en-tête. */
export function TableHeader({ className, ...props }: ComponentProps<"thead">) {
  return <thead {...props} className={joinClasses(structure.header, className)} />;
}

/** `<tbody>` : ses lignes prennent le filet et le survol des lignes de données. */
export function TableBody({ className, ...props }: ComponentProps<"tbody">) {
  return <tbody {...props} className={joinClasses(structure.body, className)} />;
}

/** `<tfoot>` : totaux calculés par le consommateur, séparés et appuyés. */
export function TableFooter({ className, ...props }: ComponentProps<"tfoot">) {
  return <tfoot {...props} className={joinClasses(structure.footer, className)} />;
}

/** `<tr>` natif : aucune sélection, aucun clic de ligne. Une ligne ne pointe nulle part ; un lien vit dans sa cellule. */
export function TableRow(props: ComponentProps<"tr">) {
  return <tr {...props} />;
}

export interface TableHeadProps extends ComponentProps<"th"> {
  /** Colonne de chiffres : libellé aligné à droite comme ses cellules. Jamais déduit des données. */
  numeric?: boolean;
}

/**
 * `<th>`. `scope` vaut `"col"` par défaut (en-tête de colonne dans `<TableHeader>`) ; un en-tête de ligne dans le corps
 * passe `scope="row"` explicitement.
 */
export function TableHead({ numeric = false, scope = "col", className, ...props }: TableHeadProps) {
  return <th {...props} scope={scope} className={joinClasses(numeric && paint.numeric, className)} />;
}

export interface TableCellProps extends ComponentProps<"td"> {
  /** Chiffres alignés à droite, chasse fixe, sans retour à la ligne. Le formatage (devise, locale) reste au consommateur. */
  numeric?: boolean;
}

/** `<td>` : contenu libre (liens, pastilles, descriptions sur plusieurs lignes). */
export function TableCell({ numeric = false, className, ...props }: TableCellProps) {
  return <td {...props} className={joinClasses(numeric && paint.numeric, className)} />;
}

/** `<caption>` visible. Pour un titre déjà affiché au-dessus, préférer `aria-labelledby` sur `<Table>`. */
export function TableCaption({ className, ...props }: ComponentProps<"caption">) {
  return <caption {...props} className={joinClasses(structure.caption, className)} />;
}

export interface TableEmptyProps extends Omit<ComponentProps<"td">, "colSpan" | "children"> {
  /** Nombre de colonnes couvertes — explicite, jamais compté depuis les enfants. */
  colSpan: number;
  /** Le message vrai du consommateur (aucun texte par défaut). */
  children: ReactNode;
}

/** Ligne d'état vide dans `<TableBody>` : une cellule sur toute la largeur. Une erreur de lecture n'est pas un vide. */
export function TableEmpty({ colSpan, className, children, ...props }: TableEmptyProps) {
  return (
    <tr>
      <td {...props} colSpan={colSpan} className={joinClasses(paint.empty, className)}>
        {children}
      </td>
    </tr>
  );
}

type TableContainerName = { label: string; labelledBy?: never } | { labelledBy: string; label?: never };

export type TableContainerProps = Omit<ComponentProps<"div">, "role" | "tabIndex" | "aria-label" | "aria-labelledby"> &
  TableContainerName;

/**
 * Défilement horizontal borné au tableau (la page ne déborde jamais). Région nommée et focusable : au clavier, les
 * flèches font défiler un tableau plus large que l'écran. Nom : `label`, ou `labelledBy` (id d'un titre visible).
 */
export function TableContainer({ label, labelledBy, className, ...props }: TableContainerProps) {
  return (
    <div
      {...props}
      role="region"
      aria-label={label}
      aria-labelledby={labelledBy}
      tabIndex={0}
      className={joinClasses(structure.container, className)}
    />
  );
}
